import { useState, useEffect, useRef } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';

export function useSSE(url, token, onAuthError) {
  const [data, setData] = useState(null);
  const ctrlRef = useRef(null);

  useEffect(() => {
    if (!token || !url) return;

    ctrlRef.current = new AbortController();
    let isMounted = true;

    const connectSse = async () => {
      while (isMounted) {
        try {
          await fetchEventSource(url, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'text/event-stream'
            },
            signal: ctrlRef.current.signal,
            openWhenHidden: true,

            async onopen(response) {
              if (response.ok) {
                console.log('SSE connection established');
                return;
              } else if (response.status === 401) {
                console.error('SSE Auth error (401)');
                if (onAuthError) {
                  onAuthError();
                }
                throw new Error('UNAUTHORIZED');
              } else {
                console.error(`SSE connection error: ${response.status}`);
                throw new Error(`CONNECTION_ERROR_${response.status}`);
              }
            },

            onmessage(event) {
              if (!event.data || event.data === ':keep-alive') return;

              try {
                const parsedData = JSON.parse(event.data);
                setData(parsedData);
              } catch (e) {
                console.error('Error parsing SSE event data', e, event.data);
              }
            },

            onerror(err) {
              if (err.name === 'AbortError') {
                throw err; // Stop retrying if aborted
              }
              console.error('SSE connection failed or error encountered:', err);
              // We throw to let fetchEventSource handle internal retries
              // If it fails too many times, it might propagate to the catch block below
              throw err;
            },

            onclose() {
              console.log('SSE connection closed by server');
              // Throwing or just letting it finish will trigger the while loop retry
            }
          });
        } catch (err) {
          if (err.name === 'AbortError') {
            console.log('SSE connection aborted intentionally');
            break;
          }

          console.error(
            'SSE encountered a fatal error or fetchEventSource gave up. Retrying in 5s...',
            err
          );

          if (!isMounted) break;

          // Manual wait before retrying if the library gives up
          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      }
    };

    connectSse();

    return () => {
      isMounted = false;
      if (ctrlRef.current) {
        ctrlRef.current.abort();
      }
    };
  }, [url, token]);

  return data;
}
